import { useState } from "react";
import { Button } from "@/components/ui/button";
import { LEVELS, VISAS } from "@/lib/api";

const sep = (k) => (k === "target_locations" ? ";" : ",");
const LISTS = ["target_locations", "target_companies", "avoid_companies", "industries"];
export const EMPTY_CLIENT = {
  name: "", email: "", phone: "", visa_status: "", visa_expiry: "", current_location: "", target_locations: [],
  relocation: null, remote_preference: "", target_titles: [], seniority: "Mid", target_companies: [],
  avoid_companies: [], industries: [], salary_floor: "", notice_period: "", linkedin_url: "", portfolio_url: "",
};

const inp = "w-full bg-slate-900 border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-blue-500";

function F({ label, children }) {
  return <label className="block"><span className="label-xs block mb-1">{label}</span>{children}</label>;
}

export default function ClientForm({ initial, onSubmit, submitLabel = "Save intake" }) {
  const [c, setC] = useState(() => {
    const base = { ...EMPTY_CLIENT, ...initial };
    LISTS.forEach((k) => { base[k] = (base[k] || []).join(sep(k) + " "); });
    base.t1 = base.target_titles?.[0] || "";
    base.t2 = base.target_titles?.[1] || "";
    return base;
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setC({ ...c, [k]: e.target.value });
  const txt = (k, label, extra = {}) => (
    <F label={label}><input data-testid={`intake-${k.replace(/_/g, "-")}-input`} className={inp} value={c[k] || ""} onChange={set(k)} {...extra} /></F>
  );
  const sel = (k, label, opts) => (
    <F label={label}>
      <select data-testid={`intake-${k.replace(/_/g, "-")}-select`} className={inp} value={c[k] ?? ""} onChange={set(k)}>
        <option value="">Select…</option>{opts.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </F>
  );

  const submit = async (e) => {
    e.preventDefault();
    const out = { ...c };
    LISTS.forEach((k) => { out[k] = c[k].split(sep(k)).map((x) => x.trim()).filter(Boolean); });
    out.target_titles = [c.t1, c.t2].map((x) => x.trim()).filter(Boolean);
    out.relocation = c.relocation === "yes" || c.relocation === true ? true : c.relocation === "no" || c.relocation === false ? false : null;
    delete out.t1; delete out.t2; delete out.jobs;
    setSaving(true);
    try { await onSubmit(out); } finally { setSaving(false); }
  };
  const relo = c.relocation === true ? "yes" : c.relocation === false ? "no" : c.relocation || "";

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="intake-form">
      <section className="grid grid-cols-3 gap-4">
        {txt("name", "Full name", { required: true })}{txt("email", "Email", { type: "email" })}{txt("phone", "Phone")}
        {txt("linkedin_url", "LinkedIn URL")}{txt("portfolio_url", "Portfolio URL")}{txt("current_location", "Current location (City, ST)")}
      </section>
      <section className="grid grid-cols-3 gap-4">
        {sel("visa_status", "Visa status", VISAS)}
        {txt("visa_expiry", "Visa / EAD expiry", { type: "month" })}
        <F label="Open to relocation">
          <select data-testid="intake-relocation-select" className={inp} value={relo} onChange={(e) => setC({ ...c, relocation: e.target.value })}>
            <option value="">Select…</option><option value="yes">Yes</option><option value="no">No</option>
          </select>
        </F>
        {txt("target_locations", "Target locations (separate with ;)")}
        {sel("remote_preference", "Remote preference", ["Remote", "Hybrid", "On-site", "Any"])}
        {sel("seniority", "Seniority", LEVELS)}
      </section>
      <section className="grid grid-cols-3 gap-4">
        {txt("t1", "Target title, track 1", { required: true })}{txt("t2", "Target title, track 2 (optional)")}
        {txt("industries", "Industries (comma separated)")}
        {txt("target_companies", "Target companies")}{txt("avoid_companies", "Companies to avoid")}
        <div className="grid grid-cols-2 gap-3">{txt("salary_floor", "Salary floor (USD)")}{txt("notice_period", "Notice period")}</div>
      </section>
      <Button data-testid="intake-save-button" type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-500 text-white">
        {saving ? "Saving…" : submitLabel}
      </Button>
    </form>
  );
}
